import React, { useEffect } from 'react'
import 'form-pattern/dist/main.css'
import Loader from '../Loader'

interface ModalProps {
  isOpen: boolean
  title?: string
  onClose?: () => void
  clearBtn?: string
  submitBtn?: string
  backBtn?: string
  saveBtn?: string
  iconSrc?: string
  iconAlt?: string
  /**
   * Called when the primary button is clicked. May be async (e.g. an API call).
   * The modal waits for it to settle before auto-closing, so a `Loader` rendered
   * in `children` and gated on the caller's own loading state stays visible for
   * the duration of the call. Return `false` (or resolve to `false`) to keep the
   * modal open afterward — e.g. to show an error and let the user retry. Any
   * other return value (including void/undefined, for existing sync handlers)
   * closes the modal as before.
   */
  onPrimary?: () => void | boolean | Promise<void | boolean>
  onSecondary?: () => void
  onBack?: () => void
  onSaveDraft?: () => void
  /**
   * Common place to show a loading state while `onPrimary` is in flight (e.g.
   * an API call) — pass the caller's own `loading` flag (from the hook driving
   * the call) instead of rendering a `Loader` in `children` per-caller. Shows
   * a full-screen `Loader` over the modal and disables the primary button for
   * the duration, on top of `primaryDisabled`.
   */
  loading?: boolean
  primaryDisabled?: boolean
  secondaryDisabled?: boolean
  children?: React.ReactNode
  containerClassName?: string
  containerStyle?: React.CSSProperties
  primaryBtnClassName?: string
  buttonsClassName?: string
  /** When true, clicking the backdrop overlay will NOT close the modal. Defaults to false. */
  disableBackdropClick?: boolean
  /**
   * Override the primary button variant. Defaults to `'primary'` which renders
   * `button -primary -light`. Pass `'secondary'` to render `button -secondary`
   * (no `-light` modifier). Has no effect when the action is destructive.
   */
  primaryBtnVariant?: 'primary' | 'secondary'
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  title = '',
  onClose,
  clearBtn,
  submitBtn,
  backBtn,
  saveBtn,
  iconSrc,
  iconAlt,
  onPrimary,
  onSecondary,
  onBack,
  onSaveDraft,
  loading = false,
  primaryDisabled = false,
  secondaryDisabled = false,
  children,
  containerClassName = '',
  containerStyle = {},
  primaryBtnClassName = '',
  buttonsClassName = '',
  disableBackdropClick = false,
  primaryBtnVariant = 'primary',
}) => {
  useEffect(() => {
    if (isOpen && typeof globalThis !== 'undefined') {
      globalThis.dispatchEvent(new CustomEvent('app-modal-open'))
    }
    return () => {
      if (typeof globalThis !== 'undefined') {
        globalThis.dispatchEvent(new CustomEvent('app-modal-close'))
      }
    }
  }, [isOpen, title])

  if (!isOpen) return null

  const closeAndEmit = () => {
    if (typeof globalThis !== 'undefined') {
      globalThis.dispatchEvent(new CustomEvent('app-modal-close'))
    }
    // prefer explicit onClose, otherwise fallback to onSecondary (cancel) so parent toggles state
    if (onClose) {
      onClose()
    } else if (onSecondary) {
      onSecondary()
    }
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // When disableBackdropClick is true the overlay click must not close the modal
    if (disableBackdropClick) return
    if (e.target === e.currentTarget) {
      closeAndEmit()
    }
  }

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      closeAndEmit()
    }
  }

  // determine which footer buttons to show. Only render a button when
  // the corresponding label or handler is explicitly provided.
  const showBack = Boolean(backBtn || clearBtn || onBack || onSecondary)
  const showSave = Boolean(saveBtn || onSaveDraft)
  const showPrimary = Boolean(submitBtn || onPrimary)

  // primary label and whether it's destructive (only used if primary is shown)
  const primaryLabel = submitBtn || ''
  const isDestructivePrimary = typeof primaryLabel === 'string' && (
    primaryLabel.toLowerCase().includes('cancel') ||
    primaryLabel.toLowerCase().includes('delete') ||
    primaryLabel.toLowerCase().includes('remove')
  )
  const variantClass = primaryBtnVariant === 'secondary' ? '-secondary' : '-primary -light'
  // `loading` (an in-flight onPrimary call) disables the primary button same as
  // primaryDisabled — prevents double-submitting while e.g. an API call is pending.
  const isPrimaryDisabled = primaryDisabled || loading
  const primaryBtnClass = `button ${isDestructivePrimary ? '-impact-destructive -light' : variantClass} modal-btn1 modal__cancel-btn ds-modal-btn1 ${isPrimaryDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${primaryBtnClassName}`

  const modal = (
    <div
      aria-hidden="false"
      className="modal-pattern ds-modal ds-grid__container ds-modal--columns-4"
      id="modal-manual-trig"
      data-ds-theme="vessel"
      data-ds-variant="windowed"
      data-ds-version="2"
      data-analytics-location="modal-example-modal-two"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2147483649, padding: '1rem 0' }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="modal-fade-screen ds-modal__fade-screen"
        onClick={handleBackdropClick}
        onKeyDown={handleBackdropKeyDown}
        tabIndex={-1}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 2147483649 }}
      ></div>
      <div
        className={`modal-container rows ds-modal__container ${containerClassName}`}
        style={{
          position: 'relative',
          zIndex: 2147483650,
          width: 'min(95vw, 40rem)',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 20px 60px rgba(13,28,61,0.25)',
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          ...containerStyle
        }}
      >
        <div
          className="modal-inner modal-icons-enabled columns-8 w-full ds-modal__inner ds-modal__icons-enabled"
          aria-labelledby="modal-header-alert"
          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
        >

          <div className="modal-header ds-modal__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid rgba(13,28,61,0.06)', flexWrap: 'wrap' }}>
            {title ? <h2 className="modal-title ds-modal__title text-base md:text-lg font-semibold" id="modal-header-alert">{title}</h2> : <h2 className="modal-title ds-modal__title text-base md:text-lg font-semibold" id="modal-header-alert">Modal</h2>}

            <button aria-label="Close" data-dismiss="x-close" type="button" className="-close--small" onClick={closeAndEmit} data-analytics-type="button" data-analytics-click="Close [X] Modal">
              <img alt="close icon" src="/assets/images/close-outline.svg" width="30" height="30" />
            </button>
          </div>
          
          <div className="modal-content ds-modal__content" style={{ paddingTop: '1rem', paddingBottom: '1rem', display: 'block', width: '100%' }}>
            <div className="modal-content-body ds-modal__content-body" style={{ display: 'block', width: '100%' }}>
              <div className="modal-content-inner ds-modal__content-inner" style={{ padding: '0.25rem 0', display: 'block', width: '100%', whiteSpace: 'normal', lineHeight: 1.4 }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {iconSrc && (
                    <img className="w-10 h-10 shrink-0" src={iconSrc} alt={iconAlt || ''} />
                  )}
                  <div style={{ flex: 1, minWidth: '250px' }}>{children}</div>
                </div>
              </div>
            </div>
            <div className="modal-actions ds-modal__actions">
              <div className={`modal-buttons -right ds-modal__buttons flex flex-col md:flex-row gap-2 md:gap-3 justify-end flex-wrap ${buttonsClassName}`}>
                {showBack && (
                  <button
                    className={`button -inverted modal-back-btn w-full md:w-auto min-w-0 whitespace-normal ${secondaryDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    type="button"
                    onClick={() => {
                      if (!secondaryDisabled) {
                        onBack?.()
                        closeAndEmit()
                      }
                    }}
                    disabled={secondaryDisabled}
                  >
                    {backBtn || clearBtn}
                  </button>
                )}

                {showSave && (
                  <button
                    className={`button -secondary modal-save-btn w-full md:w-auto min-w-0 whitespace-normal ${secondaryDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    type="button"
                    onClick={() => {
                      if (!secondaryDisabled) {
                        onSaveDraft?.()
                        closeAndEmit()
                      }
                    }}
                    disabled={secondaryDisabled}
                  >
                    {saveBtn}
                  </button>
                )}

                {showPrimary && (
                  <button
                    className={`${primaryBtnClass} w-full md:w-auto min-w-0 inline-flex items-center justify-center whitespace-normal`}
                    type="button"
                    onClick={async () => {
                      if (!isPrimaryDisabled) {
                        const result = await onPrimary?.()
                        if (result !== false) {
                          closeAndEmit()
                        }
                      }
                    }}
                    disabled={isPrimaryDisabled}
                  >
                    {isDestructivePrimary && (
                      <img className="w-6 h-6 mr-2 align-middle shrink-0" src="/assets/images/trash-outline.svg" alt="remove" />
                    )}
                    <span>{primaryLabel}</span>
                  </button>
                )}

              </div>
            </div>
          </div>
          {loading && <Loader />}
        </div>
      </div>
    </div>
  )

  return (
    <div>
      {modal}
    </div>
  )
}

export default Modal
